import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@shared/services/notification.service';
import { SyncMonitoringService } from '../sync-monitoring.service';
import { buildSyncResetRequest } from '../sync-monitoring.utils';

/** Dialog payload. `tables` are the tables that can be reset (name + self-link, which the request filter needs); `selected` preselects some of them by name, e.g. the one table "Reset Sync" was chosen for or the tables a repair-needed record names. Nothing preselected starts on all tables. */
export interface IResetSyncDialogData {
	environmentId: string;
	environmentName: string;
	tables: { name: string; self: string }[];
	selected?: string[];
}

/** Resets the synchronization of all tables or of the selected ones, and closes with true once the reset is requested. */
@Component({
	selector: 'app-reset-sync-dialog',
	templateUrl: './reset-sync-dialog.component.html',
	styleUrls: ['./reset-sync-dialog.component.scss', '../audit-dialogs.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ResetSyncDialogComponent {
	public isLoading = false;
	public allTables: boolean;
	public readonly selected: Set<string>;
	public readonly environmentName: string;
	public rowVersionForm: FormGroup;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IResetSyncDialogData,
		public dialogRef: MatDialogRef<ResetSyncDialogComponent, boolean>,
		private fb: FormBuilder,
		private syncMonitoringService: SyncMonitoringService,
		private notificationService: NotificationService,
	) {
		this.selected = new Set(data.selected ?? []);
		this.allTables = this.selected.size === 0;
		this.environmentName = data.environmentName;
		this.rowVersionForm = this.fb.group({
			rowVersion: [0, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
			// Only meaningful for a complete (all-tables) reset — see `isCompleteReset`. The template
			// requires it checked before enabling Confirm, as the safeguard against an accidental
			// full resync.
			confirmComplete: [false],
		});
	}

	get isRowVersionValid(): boolean {
		return this.rowVersionForm.controls['rowVersion'].valid;
	}

	/** A reset of all tables (empty filter) — the dangerous case. */
	get isCompleteReset(): boolean {
		return this.allTables;
	}

	/** Confirm is allowed only when the row version is valid and either at least one table is selected or, for a complete reset, the user has explicitly acknowledged the all-tables safeguard checkbox. */
	get canConfirm(): boolean {
		if (!this.isRowVersionValid || this.isLoading) {
			return false;
		}
		return this.isCompleteReset ? this.rowVersionForm.controls['confirmComplete'].value === true : this.selected.size > 0;
	}

	toggleTable(table: string, checked: boolean): void {
		if (checked) {
			this.selected.add(table);
		} else {
			this.selected.delete(table);
		}
		this.allTables = false;
	}

	/** Builds the reset request (empty filter = all tables; otherwise one element per selected table) and posts it. */
	onConfirm(): void {
		if (!this.canConfirm) {
			return;
		}

		// Safeguard: a table-scoped reset MUST carry a filter element for every selected table. If a
		// self-link is missing we refuse rather than drop the table, or — with none left — fall
		// through to buildSyncResetRequest's empty filter, which would silently escalate the reset
		// into an ALL-tables reset.
		const tableSelves = this.isCompleteReset ? [] : this.data.tables.filter((table) => this.selected.has(table.name) && table.self).map((table) => table.self);
		if (!this.isCompleteReset && tableSelves.length !== this.selected.size) {
			this.notificationService.notify(['administration.sync-monitoring.reset-dialog.errors.missing-filter'], [], 'error');
			return;
		}

		const request = buildSyncResetRequest(tableSelves, Number(this.rowVersionForm.controls['rowVersion'].value));

		// Belt-and-suspenders: only a complete reset may have an empty filter.
		if (!this.isCompleteReset && request.filter.length === 0) {
			this.notificationService.notify(['administration.sync-monitoring.reset-dialog.errors.missing-filter'], [], 'error');
			return;
		}

		// Escape or a backdrop click would close the dialog before close(true), and the page would miss the reload.
		this.setLoading(true);
		this.syncMonitoringService.resetSync(this.data.environmentId, request).subscribe({
			next: () => this.dialogRef.close(true),
			error: () => {
				this.setLoading(false);
				this.notificationService.notify(['administration.sync-monitoring.reset-dialog.errors.reset-failed'], [], 'error');
			},
		});
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	/** Only Cancel closes the dialog while the reset runs, and Cancel is disabled then. */
	private setLoading(loading: boolean): void {
		this.isLoading = loading;
		this.dialogRef.disableClose = loading;
	}
}
